import { SensorManagerPlugin, SensorManagerPluginPlugin } from "@WahooFitness/wsm-native";
import { PropsWithChildren, createContext, useCallback, useMemo, useRef, useState } from "react";
import { IWsmPluginProps } from "@/contexts/WsmContext";
import { WSMLiveTrackStatus } from "@WahooFitness/wsm-native/dist/esm/types/live_track_status";
import WSMSensorConnectionState from "@WahooFitness/wsm-native/dist/esm/types/connection_state";
import { WebWsmDevice } from "@/services/WebWsmDevice";
import { useWifiMock } from "@/components/Elemnt/Wifi/useWifiMock";
import { packages, customerInfo } from "../offeringsMockData/offerings";
import { KCruxSensorProductType } from "@WahooFitness/wsm-native/dist/esm/types/kc/KCruxSensorProductType";
import WSMSensorLocation from "@WahooFitness/wsm-native/dist/esm/types/sensor_location";
import {
  WSMRemoteSensor,
  WSMRemoteSensorCfg,
  WSMRemoteSensorStatusResult,
} from "@WahooFitness/wsm-native/dist/esm/types/remote_sensor_status";
import { WSMWheelCircumferences } from "@WahooFitness/wsm-native/dist/esm/types/wheel_circumference_cfg";
import { WSMGearCfg } from "@WahooFitness/wsm-native/dist/esm/types/gear_cfg";
import { WSMBoltFwuUpgradeState } from "@WahooFitness/wsm-native/dist/esm/types/bolt_firmware";
import {
  WSMBikeBellFileTransferResult,
  WSMBikeBellSound,
  WSMBoltAppPasscodeState,
} from "@WahooFitness/wsm-native/dist/esm/types/bolt_cfg";
import { WSMRemoteCfgStatus } from "@WahooFitness/wsm-native/dist/esm/types/remote_config_cfg_status";
const mockRemoteSensors: number[] = [
  KCruxSensorProductType.WAHOO_BLUESC,
  KCruxSensorProductType.WAHOO_TRACKR_CADENCE,
  KCruxSensorProductType.WAHOO_KICKR_BIKE,
  KCruxSensorProductType.WAHOO_KICKR_BIKE_SHIFT,
  KCruxSensorProductType.WAHOO_KICKR_CORE,
  KCruxSensorProductType.WAHOO_HEADWIND,
  KCruxSensorProductType.WAHOO_KICKR_MOVE,
  KCruxSensorProductType.WAHOO_KICKR_ROLLR,
  KCruxSensorProductType.WAHOO_KICKR_SNAP,
  KCruxSensorProductType.WAHOO_KICKR,
  KCruxSensorProductType.WAHOO_POWER_PEDALS_LEFT,
  KCruxSensorProductType.WAHOO_POWER_PEDALS_RIGHT,
  KCruxSensorProductType.WAHOO_RPM_SPEED,
  KCruxSensorProductType.WAHOO_TICKR,
  KCruxSensorProductType.WAHOO_TICKR_FIT,
  KCruxSensorProductType.WAHOO_TICKR_X,
  KCruxSensorProductType.WAHOO_TRACKR_HR,
  KCruxSensorProductType.WAHOO_TRACKR_RADAR,
  KCruxSensorProductType.GARMIN_HRM_DUAL,
  KCruxSensorProductType.SHIMANO_DI2,
  KCruxSensorProductType.GENERIC_TYRE_PRESSURE,
  KCruxSensorProductType.FLOWBIO,
  // DEX-788: a paired Tymewear VitalPro breathing sensor so useHasElemntWithTymewear() is true on web.
  KCruxSensorProductType.VITALPRO_BREATHING,
];

const wheelCircumferenceOptions: WSMWheelCircumferences = {
  wheels: [
    {
      circumference: 2070,
      description: "700 x 18C",
      description_iso: "18-622",
    },
    {
      circumference: 2080,
      description: "700 x 19C",
      description_iso: "19-622",
    },
    {
      circumference: 2090,
      description: "700 x 20C",
      description_iso: "20-622",
    },
    {
      circumference: 2100,
      description: "700 x 21C",
      description_iso: "21-622",
    },
    {
      circumference: 2110,
      description: "700 x 22C",
      description_iso: "22-622",
    },
    {
      circumference: 2120,
      description: "700 x 23C",
      description_iso: "23-622",
    },
    {
      circumference: 2130,
      description: "700 x 24C",
      description_iso: "24-622",
    },
    {
      circumference: 2140,
      description: "700 x 25C",
      description_iso: "25-622",
    },
  ],
};

const bikeGearCfgs: WSMGearCfg = {
  "9": {
    cassettes: [
      {
        description: "11-32T",
        gears: [32, 18, 17, 16, 15, 14, 13, 12, 11],
      },
      {
        description: "12-25T",
        gears: [25, 23, 21, 19, 17, 15, 14, 13, 12],
      },
      {
        description: "11-28T",
        gears: [28, 25, 23, 21, 19, 17, 15, 13, 11],
      },
      {
        description: "12-30T",
        gears: [30, 27, 25, 22, 20, 18, 16, 14, 12],
      },
    ],
  },
  "10": {
    cassettes: [
      {
        description: "11-32T",
        gears: [32, 19, 18, 17, 16, 15, 14, 13, 12, 11],
      },
      {
        description: "12-25T",
        gears: [25, 24, 23, 21, 19, 17, 15, 14, 13, 12],
      },
      {
        description: "11-28T",
        gears: [28, 26, 24, 22, 20, 18, 16, 14, 12, 11],
      },
      {
        description: "12-30T",
        gears: [30, 28, 26, 24, 22, 19, 17, 15, 13, 12],
      },
    ],
  },
  "11": {
    cassettes: [
      {
        description: "11-32T",
        gears: [32, 21, 19, 18, 17, 16, 15, 14, 13, 12, 11],
      },
      {
        description: "12-25T",
        gears: [25, 24, 23, 21, 19, 17, 16, 15, 14, 13, 12],
      },
      {
        description: "11-28T",
        gears: [28, 26, 24, 22, 20, 18, 16, 14, 13, 12, 11],
      },
      {
        description: "12-30T",
        gears: [30, 27, 25, 23, 21, 19, 17, 15, 14, 13, 12],
      },
    ],
  },
  "12": {
    cassettes: [
      {
        description: "11-32T",
        gears: [32, 23, 22, 19, 18, 17, 16, 15, 14, 13, 12, 11],
      },
      {
        description: "12-25T",
        gears: [25, 23, 22, 21, 19, 18, 17, 16, 15, 14, 13, 12],
      },
      {
        description: "11-28T",
        gears: [28, 25, 23, 21, 19, 17, 16, 15, 14, 13, 12, 11],
      },
      {
        description: "12-30T",
        gears: [30, 28, 25, 23, 21, 19, 17, 16, 15, 14, 13, 12],
      },
    ],
  },
  "13": {
    cassettes: [
      {
        description: "11-32T",
        gears: [32, 24, 24, 23, 19, 18, 17, 16, 15, 14, 13, 12, 11],
      },
      {
        description: "11-34T",
        gears: [34, 31, 28, 26, 24, 22, 20, 18, 16, 14, 13, 12, 11],
      },
      {
        description: "10-33T",
        gears: [33, 30, 27, 25, 23, 21, 19, 17, 15, 13, 12, 11, 10],
      },
      {
        description: "11-36T",
        gears: [36, 34, 31, 28, 25, 23, 21, 19, 17, 15, 13, 12, 11],
      },
    ],
  },
};

const sensorCfg: WSMRemoteCfgStatus = {
  sensor_id: 1,
  app_token: "ELEMNT ACE 0D74",
  bolt_type: 8,
  sensor_product_type: 111,
  hw: 5,
  cfg: {
    app: {
      activity_feedback: true,
      alert_email: false,
      alert_msg: false,
      alert_phone: false,
      auto_control_bike_lights: true,
      auto_reroute: true,
      auto_update: false,
      auto_upload: true,
      buzz_notif: true,
      custom_alerts: true,
      display_cfg_hide_titles: false,
      follow_with_heading: true,
      gps_pos_assist_data_req: false,
      gps_pos_assist_valid: true,
      kickr_mode_override_allowed: true,
      launch_ready_to_ride: true,
      map_upgrade_required: false,
      mute: false,
      release_channel: "",
      sn: "57243600043",
      version: "WG37-301063",
      app_profile: 2,
      auto_shutdown_duration_min: 15,
      backlight_brightness_perc: 67,
      backlight_cfg: 0,
      backlight_duration_sec: 5,
      battery_perc: 78,
      battery_status: 2,
      body_position: 0,
      button_squeeze_cfg: 0,
      current_cfg_profile_id: 18,
      dnd_end_time_sec: 0,
      dnd_interval_sec: 0,
      dnd_schedule_end_time_min: 420,
      dnd_schedule_start_time_min: 1320,
      first_run_state: 1,
      last_interaction_time_sec: 0,
      led_mode_override: 0,
      log_level: 2,
      theme: 1,
      theme_tint_color: 0,
      upgrade_download_perc: 255,
      upgrade_state: 2,
      upgrade_state_rom: 2,
      user_outdoor_mode: 1,
      volume: 33,
      wifi_nw_count: 2,
      workout_page_color_mode: 0,
      usb_access_enabled: true,
      usb_fit_export_enabled: true,
      passcode_state: 1,
      bike_bell_enabled: true,
      bike_bell_sound: WSMBikeBellSound.Classic,
      bike_bell_custom_file: "",
      bike_bell_custom_files: "cowbell.mp3/ding.wav",
      radar_cfg: {
        alerts_enabled: true,
        alerts_on_left_leds_enabled: false,
        alerts_on_top_leds_enabled: true,
        alerts_on_screen_enabled: true,
        alerts_on_screen_right_enabled: false,
        alert_sounds_enabled: true,
        light_auto_off_duration_sec: 0,
      },
    },
  },
  features: {
    boltapp_backlight_auto_max: true,
    usb_access: true,
    usb_fit_export: true,
    passcode: true,
    smart_light_control: true,
    bike_bell_cfg: true,
  },
};

const initialBikeBellCustomFiles = (sensorCfg.cfg.app.bike_bell_custom_files ?? "")
  .split("/")
  .filter(Boolean);

export const WsmContext = createContext<IWsmPluginProps | undefined>(undefined);

export const WsmContextProvider = ({ children }: PropsWithChildren) => {
  const { wifiNetworks, wifiEnabled } = useWifiMock({ emptyScan: false });

  const [cogToothCounts, setCogToothCounts] = useState<number[]>([
    32, 23, 22, 19, 18, 17, 16, 15, 14, 13, 12, 11,
  ]);

  const [bikeBellEnabled, setBikeBellEnabled] = useState(
    sensorCfg.cfg.app.bike_bell_enabled ?? false
  );
  const [bikeBellSound, setBikeBellSound] = useState({
    sound: sensorCfg.cfg.app.bike_bell_sound ?? WSMBikeBellSound.Classic,
    customFile: sensorCfg.cfg.app.bike_bell_custom_file ?? "",
  });
  // A ref, not state: the hook revalidates the list as soon as a send resolves, before a re-render.
  const bikeBellCustomFiles = useRef(initialBikeBellCustomFiles);

  const sendSetRemoteSensorCfg = useCallback(({ cfg }: { cfg: WSMRemoteSensorCfg }) => {
    setCogToothCounts(cfg.bike_gear_cfg?.rear?.cog_tooth_counts ?? []);
    return Promise.resolve({ completed: true });
  }, []);

  const webPluginOverride = useMemo((): Record<string, any> => {
    return {
      getLiveTrackStatus: async (): Promise<{
        isEnabled: boolean;
        isFeatureEnabled: boolean;
        isPermitted: boolean;
        tracking?: WSMLiveTrackStatus;
      }> => ({
        isEnabled: true,
        isFeatureEnabled: true,
        isPermitted: true,
        tracking: {
          isLocal: true,
          appToken: "test",
          workoutNum: 1,
          isCommsDown: false,
          commsDownCount: 0,
          noNetworkSec: 0,
          sendFailedCount: 0,
          sendOkCount: 0,
          slowSendSec: 0,
          totalSec: 0,
          secSinceLastSendOk: 65,
          elemnt: {
            sensorConnectionState: WSMSensorConnectionState.CONNECTED,
          },
        },
      }),
      getDevices: async () => ({ devices: [WebWsmDevice] }),
      unpairFromDevice: async () => ({
        completed: true,
      }),
      bcGetBoltWifiNetworks: async () => wifiNetworks,
      bcIsBoltWifiAllowed: async () => ({ allowed: wifiEnabled }),
      bcGetFirmwareStatus: async () => ({ upgradeState: WSMBoltFwuUpgradeState.UpToDate }),
      getWorkoutProfiles: async () => ({
        workoutProfiles: [],
      }),
      getRevenueCatOfferings: async () => ({ packages, customerInfo }),
      makeRevenueCatPurchase: async () => ({
        completed: true,
      }),
      sendSetRemoteSensorCfg,
      getRemoteSensorStatus: async (): Promise<{ result: WSMRemoteSensorStatusResult }> => {
        return {
          result: {
            manager: {
              is_charging: false,
              is_discovering_ant: false,
              is_discovering_btle: false,
              ant_hardware_state: 0,
              btle_hardware_state: 0,
              battery_perc: 0,
            },
            sensors: mockRemoteSensors.map(
              (sensor, index): WSMRemoteSensor => ({
                remote_sensor_id: [index],
                status: {
                  remote_sensor_id: [index],
                  is_available: false,
                  is_charging: false,
                  is_paired: true,
                  fw: "",
                  hw: "",
                  fwu_result: 0,
                  fwu_state: 0,
                  connection_state: WSMSensorConnectionState.CONNECTED,
                  sensor_product_type:
                    sensor === KCruxSensorProductType.WAHOO_POWER_PEDALS_RIGHT
                      ? KCruxSensorProductType.WAHOO_POWER_PEDALS_LEFT
                      : sensor,
                  batt_perc: 50,
                  rssi: 0,
                },
                sensors:
                  sensor === KCruxSensorProductType.WAHOO_POWER_PEDALS_RIGHT
                    ? [
                        {
                          remote_sensor_id: [index],
                          status: {
                            remote_sensor_id: [index],
                            is_available: false,
                            is_charging: false,
                            is_paired: true,
                            fw: "",
                            hw: "",
                            fwu_result: 0,
                            fwu_state: 0,
                            connection_state: WSMSensorConnectionState.CONNECTED,
                            sensor_product_type: sensor,
                            batt_perc: 50,
                            rssi: 0,
                          },
                          sensors: [],
                          fw: "",
                          fwu_required: false,
                          proxy_sensor_id: 0,
                          cfg: {
                            sensor_id: 0,
                            display_name: "",
                          },
                        },
                      ]
                    : [],
                fw: "",
                fwu_required: false,
                proxy_sensor_id: 0,
                cfg:
                  sensor === KCruxSensorProductType.FLOWBIO
                    ? {
                        sensor_id: index,
                        display_name: "",
                        sensor_location: WSMSensorLocation.UPPER_BACK,
                      }
                    : {
                        sensor_id: index,
                        display_name: "",
                        wheel_circ_mm: 1725,
                        crank_length_m: 0.1775,
                        pm_auto_calib: true,
                        is_wheel_circ_auto_calib_enabled: false,
                        sensor_location: 4,
                        bike_gear_cfg: {
                          front: {
                            is_front: true,
                            cog_tooth_counts: [28, 34, 52],
                          },
                          rear: {
                            is_front: false,
                            cog_tooth_counts: cogToothCounts,
                          },
                        },
                      },
              })
            ),
          },
        };
      },
      sendForgetRemoteSensor: async () => ({ completed: true }),
      getRemoteCfgStatus: async () => ({ result: sensorCfg }),
      getWheelCircumferenceCfgOptions: async () => ({ cfg: wheelCircumferenceOptions }),
      getBikeGearCfgOptions: async () => ({ cfg: bikeGearCfgs }),
      getNativeAppPermissionEnabled: async () => ({ enabled: false }),
      bcGetUsbAccessEnabled: async () => ({
        enabled: sensorCfg.cfg.app.usb_access_enabled ?? false,
      }),
      bcGetUsbFitExportEnabled: async () => ({
        enabled: sensorCfg.cfg.app.usb_fit_export_enabled ?? false,
      }),
      bcGetPasscodeState: async () => ({
        state: sensorCfg.cfg.app.passcode_state ?? WSMBoltAppPasscodeState.Enabled,
      }),
      bcGetBikeBellEnabled: async () => ({ enabled: bikeBellEnabled }),
      bcSetBikeBellEnabled: async ({ enabled }: { enabled: boolean }) => {
        setBikeBellEnabled(enabled);
        return { completed: true };
      },
      bcGetBikeBellSound: async () => bikeBellSound,
      bcSetBikeBellSound: async ({
        sound,
        customFile,
      }: {
        sound: WSMBikeBellSound;
        customFile?: string;
      }) => {
        setBikeBellSound((previous) => ({ sound, customFile: customFile ?? previous.customFile }));
        return { completed: true };
      },
      bcGetBikeBellCustomFiles: async () => ({ files: bikeBellCustomFiles.current }),
      bcPlayBikeBell: async () => {
        console.log("bcPlayBikeBell: would ring the bell on the ELEMNT");
        return { completed: true };
      },
      bcSendBikeBellFile: async ({ fileName }: { fileName: string }) => {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (!bikeBellCustomFiles.current.includes(fileName)) {
          bikeBellCustomFiles.current = [...bikeBellCustomFiles.current, fileName];
        }
        return { completed: true, result: WSMBikeBellFileTransferResult.Success };
      },
      bcDeleteBikeBellFile: async ({ fileName }: { fileName: string }) => {
        await new Promise((resolve) => setTimeout(resolve, 800));
        bikeBellCustomFiles.current = bikeBellCustomFiles.current.filter(
          (name) => name !== fileName
        );
        return { completed: true, result: WSMBikeBellFileTransferResult.Success };
      },
    };
  }, [
    bikeBellEnabled,
    bikeBellSound,
    cogToothCounts,
    sendSetRemoteSensorCfg,
    wifiEnabled,
    wifiNetworks,
  ]);

  const wsmProxy = useMemo(() => {
    const handler = {
      get: function (target: any, prop: string, _receiver: any) {
        const originalMethod = target[prop];
        if (typeof originalMethod === "function") {
          return function (...args: any) {
            let result: any;
            if (webPluginOverride[prop] !== undefined) {
              result = webPluginOverride[prop].apply(target, args);
            } else {
              result = originalMethod.apply(target, args);
            }
            return result;
          };
        }
        return originalMethod;
      },
    };
    return new Proxy<SensorManagerPluginPlugin>(SensorManagerPlugin, handler);
  }, [webPluginOverride]);

  return (
    <WsmContext.Provider
      value={{
        sensorManagerPlugin: wsmProxy,
      }}
    >
      {children}
    </WsmContext.Provider>
  );
};
